// /room/:code and /join/:code. Owns the GameSession for the room, shows the join form or a status
// page until this phone is seated, then hands the view to the screen for the current moment.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConnectionBanner, ToastHost } from "../components/Toast";
import { useWakeLock } from "../hooks/useDevice";
import { getRoom, isValidCode, normalizeCode, type RoomInfo } from "../net/api";
import { GameSession } from "../net/session";
import { SessionContext, useSession } from "../net/SessionContext";
import { loadName } from "../state/local";
import { useGame, type JoinMode, type SessionStatus } from "../state/store";
import { GameScreen } from "./GameScreen";

export function RoomShell() {
  const params = useParams();
  const code = normalizeCode(params.code ?? "");
  const valid = isValidCode(code);
  const session = useMemo(() => new GameSession(code), [code]);

  useEffect(() => {
    if (!valid) return;
    useGame.getState().reset(code);
    session.start();
    return () => session.stop();
  }, [session, code, valid]);

  if (!valid) return <Gate status="not_found" code={code} />;
  return (
    <SessionContext.Provider value={session}>
      <RoomContent code={code} />
      <ConnectionBanner />
      <ToastHost />
    </SessionContext.Provider>
  );
}

function RoomContent({ code }: { code: string }) {
  const status = useGame((s) => s.status);
  const view = useGame((s) => s.view);
  const inGame = !!view && view.phase !== "lobby" && (status === "live" || status === "reconnecting");
  useWakeLock(inGame);

  if ((status === "live" || status === "reconnecting") && view) return <GameScreen view={view} />;
  if (status === "need_name") return <JoinForm code={code} />;
  if (status === "idle" || status === "connecting" || status === "reconnecting") {
    return (
      <main className="screen screen-center" data-screen="connecting">
        <p className="muted">Connecting to room {code}…</p>
      </main>
    );
  }
  return <Gate status={status} code={code} />;
}

const GATE_TEXT: Partial<Record<SessionStatus, { title: string; body: string }>> = {
  not_found: {
    title: "Room not found",
    body: "Check the code. Rooms close a while after their last game.",
  },
  game_in_progress: {
    title: "Game in progress",
    body: "This game has already started. If you were playing, ask the host for a rejoin code.",
  },
  room_full: { title: "Room full", body: "This room already has 15 players." },
  superseded: {
    title: "Seat opened elsewhere",
    body: "Your seat is now connected on another device or tab.",
  },
  moved: {
    title: "Your seat moved to another phone",
    body:
      "The host gave a rejoin code for your seat, and another phone used it. " +
      "To play on this phone, ask the host for a new rejoin code.",
  },
  left: { title: "You left the room", body: "You can join again while the room is in the lobby." },
  kicked: { title: "Removed from the room", body: "The host removed you from this room." },
  closed: { title: "Room closed", body: "This room has closed." },
};

function Gate({ status, code }: { status: SessionStatus; code: string }) {
  const text = GATE_TEXT[status] ?? { title: "Disconnected", body: "" };
  const navigate = useNavigate();
  return (
    <main className="screen screen-center" data-screen={`gate-${status}`}>
      <div className="stack">
        <h2>{text.title}</h2>
        <p className="muted">{text.body}</p>
        {status === "superseded" && <ReconnectHere />}
        {status === "moved" && <EnterRejoinCode />}
        {(status === "left" || status === "game_in_progress") && (
          <button className="btn" onClick={() => navigate(0)}>
            Try again
          </button>
        )}
        <Link className="btn btn-ghost" to="/">
          Home
        </Link>
        <p className="tiny faint mono">{code}</p>
      </div>
    </main>
  );
}

/** The same seat token opened elsewhere: this phone's token still works, so it can take over. */
function ReconnectHere() {
  const session = useSession();
  return (
    <button className="btn btn-primary" onClick={() => session.start()}>
      Use this phone
    </button>
  );
}

/** A rejoin code moved the seat: this phone's token is dead, so only a new code brings it back. */
function EnterRejoinCode() {
  const session = useSession();
  return (
    <button className="btn btn-primary" onClick={() => session.enterRejoinCode()}>
      Enter a rejoin code
    </button>
  );
}

function JoinForm({ code }: { code: string }) {
  const session = useSession();
  const joinError = useGame((s) => s.joinError);
  const [name, setName] = useState(loadName);
  // the form last used (a wrong code comes back to the code form), or the one asked for
  const [mode, setMode] = useState<JoinMode>(() => useGame.getState().joinMode);
  const [reseatCode, setReseatCode] = useState("");
  const [info, setInfo] = useState<RoomInfo | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    getRoom(code)
      .then((r) => !cancelled && setInfo(r))
      .catch(() => !cancelled && setInfo(undefined));
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (info === null) return <Gate status="not_found" code={code} />;
  const inProgress = info && info.phase !== "lobby";
  const showReseat = mode === "reseat" || inProgress;
  const trimmed = name.trim();

  return (
    <main className="screen screen-center" data-screen="join">
      <div className="stack">
        <span className="eyebrow">Room</span>
        <h1 className="mono">{code}</h1>
        {info && (
          <p className="muted small">
            {inProgress
              ? "A game is in progress."
              : `${info.player_count} player${info.player_count === 1 ? "" : "s"} waiting`}
          </p>
        )}
      </div>

      {!showReseat ? (
        <form
          className="card stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed) session.join(trimmed);
          }}
        >
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input
              id="name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={20}
              autoComplete="nickname"
              autoFocus
              placeholder="What the table calls you"
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={!trimmed}>
            Take a seat
          </button>
        </form>
      ) : (
        <form
          className="card stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (reseatCode.trim().length >= 4) session.reseat(reseatCode);
          }}
        >
          <div className="field">
            <label htmlFor="reseat">Rejoin code from the host</label>
            <input
              id="reseat"
              className="input input-code"
              value={reseatCode}
              onChange={(e) => setReseatCode(e.target.value.replace(/\D/g, ""))}
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={reseatCode.length < 6}>
            Take my seat back
          </button>
        </form>
      )}
      {joinError && <p className="error-text">{joinError}</p>}
      {!inProgress && (
        <button
          className="btn btn-ghost btn-small"
          onClick={() => setMode(mode === "name" ? "reseat" : "name")}
        >
          {mode === "name" ? "I have a rejoin code" : "Join with a name instead"}
        </button>
      )}
      <Link className="btn btn-ghost btn-small" to="/">
        Home
      </Link>
    </main>
  );
}
